/**
 * PeriscolairePage — vue périscolaire du jour (matin / soir), staff.
 * Affiche les effectifs par créneau ; la gestion des inscriptions/réservations
 * se fait via la fiche enfant (espace famille) ou l'agent.
 */

import { useEffect, useState } from 'react';
import { Sunrise, Sunset } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Input, Field, Alert } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import type { PeriscolaireEffectifJour } from '@roronoa-collectivites/shared-types';

export function PeriscolairePage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [eff, setEff] = useState<PeriscolaireEffectifJour | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false; setLoading(true);
    (async () => {
      try {
        const e = await api.scolaire.periscolaire.getEffectif(date);
        if (!cancelled) setEff(e);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiException ? err.message : 'Erreur de chargement');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  return (
    <ScolaireLayout activeTab="periscolaire">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Périscolaire</h1>
          <p className="mt-1 text-sm text-ink-muted">Garderie du matin et du soir — effectifs du jour.</p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-6 max-w-xs">
          <Field label="Date" htmlFor="date-peri">
            <Input id="date-peri" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardBody>
              <div className="mb-3 flex items-center gap-2">
                <Sunrise className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                <h2 className="font-display text-base font-semibold text-ink">Matin</h2>
              </div>
              <div className="text-4xl font-semibold tabular-nums text-ink">{loading ? '—' : eff?.matin.comptes ?? 0}</div>
              <p className="mt-1 text-sm text-ink-muted">enfants attendus{!loading && eff && eff.matin.absents > 0 ? ` · ${eff.matin.absents} absent(s)` : ''}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <div className="mb-3 flex items-center gap-2">
                <Sunset className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                <h2 className="font-display text-base font-semibold text-ink">Soir</h2>
              </div>
              <div className="text-4xl font-semibold tabular-nums text-ink">{loading ? '—' : eff?.soir.comptes ?? 0}</div>
              <p className="mt-1 text-sm text-ink-muted">enfants attendus{!loading && eff && eff.soir.absents > 0 ? ` · ${eff.soir.absents} absent(s)` : ''}</p>
            </CardBody>
          </Card>
        </div>
      </main>
    </ScolaireLayout>
  );
}
